import React, { useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  Typography,
  MenuItem,
} from "@mui/material";
import { AppButton, AppTextField } from "../ui";
import {
  buildElementOptimizationPayload,
  createDefaultElementOptimizationForm,
  ELEMENT_OPTIMIZATION_METHOD_OPTIONS,
  ELEMENT_OPTIMIZATION_MODE_OPTIONS,
  getElementOptimizationModeHelperText,
  isCustomElementOptimizationMode,
  isValidElementOptimizationForm,
} from "../../utility/elementOptimization";
import {
  SUPPORTED_ELEMENT_FILE_HELPER_TEXT,
  SUPPORTED_ELEMENT_FILE_INPUT_ACCEPT,
  isElementImageFile,
  isSupportedElementFile,
} from "../../utility/elementFiles";
import usePasteFilesOnHover from "./usePasteFilesOnHover";
import "./UploadElementDialog.scss";

const UploadElementDialog = ({ open, onClose, onUpload }) => {
  const [newElementName, setNewElementName] = useState("");
  const [newElementDescription, setNewElementDescription] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [optimization, setOptimization] = useState(
    createDefaultElementOptimizationForm()
  );
  const [loading, setLoading] = useState(false);
  const [selectedFilePreviewUrl, setSelectedFilePreviewUrl] = useState("");
  const fileInputRef = useRef(null);
  const selectedFileIsImage =
    Boolean(selectedFile) &&
    (selectedFile.type?.startsWith("image/") || isElementImageFile(selectedFile.name));

  useEffect(() => {
    if (!selectedFileIsImage) {
      setSelectedFilePreviewUrl("");
      return undefined;
    }

    const previewUrl = URL.createObjectURL(selectedFile);
    setSelectedFilePreviewUrl(previewUrl);

    return () => {
      URL.revokeObjectURL(previewUrl);
    };
  }, [selectedFile, selectedFileIsImage]);

  const resetState = () => {
    setNewElementName("");
    setNewElementDescription("");
    setSelectedFile(null);
    setOptimization(createDefaultElementOptimizationForm());
  };

  const addSelectedFile = (file) => {
    try {
      if (!isSupportedElementFile(file)) {
        throw new Error(
          `Unsupported file type. ${SUPPORTED_ELEMENT_FILE_HELPER_TEXT}`
        );
      }
      setSelectedFile(file);
    } catch (error) {
      console.error(error);
      alert(error.message);
    }
  };

  const pasteHandlers = usePasteFilesOnHover({
    enabled: open && !loading,
    onFiles: (files) => {
      addSelectedFile(files[0]);
    },
  });

  // Handle drag and drop.
  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      addSelectedFile(file);
      e.dataTransfer.clearData();
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      addSelectedFile(file);
    }
  };

  const handleClickDropzone = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleClose = () => {
    if (loading) {
      return;
    }

    resetState();
    onClose();
  };

  const handleUploadClick = async () => {
    if (loading || !selectedFile || !isValidElementOptimizationForm(optimization)) {
      return;
    }

    setLoading(true);
    try {
      await onUpload({
        name: newElementName,
        description: newElementDescription,
        file: selectedFile,
        optimization: buildElementOptimizationPayload(optimization),
      });
      resetState();
    } catch (error) {
      console.error("Upload failed:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ className: "upload-element-dialog__paper" }}
    >
      <DialogTitle className="upload-element-dialog__title">Upload New Element</DialogTitle>
      <DialogContent className="upload-element-dialog__content">
        <Grid container spacing={2} className="upload-element-dialog__form">
          <Grid item xs={12}>
            <AppTextField
              label="Element Name"
              value={newElementName}
              onChange={(e) => setNewElementName(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <AppTextField
              label="Element Description"
              value={newElementDescription}
              onChange={(e) => setNewElementDescription(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <AppTextField
              select
              label="Optimization"
              value={optimization.mode}
              onChange={(e) =>
                setOptimization((prev) => ({ ...prev, mode: e.target.value }))
              }
              helperText={getElementOptimizationModeHelperText(optimization.mode)}
              fullWidth
            >
              {ELEMENT_OPTIMIZATION_MODE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </AppTextField>
          </Grid>
          {isCustomElementOptimizationMode(optimization.mode) && (
            <>
              <Grid item xs={12} sm={6}>
                <AppTextField
                  select
                  label="Method"
                  value={optimization.method}
                  onChange={(e) =>
                    setOptimization((prev) => ({
                      ...prev,
                      method: e.target.value,
                    }))
                  }
                  fullWidth
                >
                  {ELEMENT_OPTIMIZATION_METHOD_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </AppTextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <AppTextField
                  label="Target Faces"
                  type="number"
                  value={optimization.targetFaces}
                  onChange={(e) =>
                    setOptimization((prev) => ({
                      ...prev,
                      targetFaces: e.target.value,
                    }))
                  }
                  inputProps={{ min: 1, step: 1000 }}
                  helperText="Positive integer face target."
                  fullWidth
                />
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <Box
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={handleClickDropzone}
              {...pasteHandlers}
              className="upload-element-dialog__dropzone"
            >
              {selectedFile ? (
                <Box className="upload-element-dialog__selected-file">
                  {selectedFilePreviewUrl && (
                    <img
                      className="upload-element-dialog__selected-image"
                      src={selectedFilePreviewUrl}
                      alt=""
                    />
                  )}
                  <Typography className="upload-element-dialog__dropzone-text">
                    {selectedFile.name}
                  </Typography>
                </Box>
              ) : (
                <Typography className="upload-element-dialog__dropzone-text">
                  Drag and drop a file here, or click to select a file
                </Typography>
              )}
              <input
                type="file"
                accept={SUPPORTED_ELEMENT_FILE_INPUT_ACCEPT}
                style={{ display: "none" }}
                ref={fileInputRef}
                onChange={handleFileInputChange}
              />
            </Box>
            <Typography variant="caption" className="upload-element-dialog__helper-text">
              {SUPPORTED_ELEMENT_FILE_HELPER_TEXT}
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className="upload-element-dialog__actions">
        <AppButton
          kind="ghost"
          onClick={handleClose}
          disabled={loading}
          className="upload-element-dialog__cancel"
        >
          Cancel
        </AppButton>
        <AppButton
          onClick={handleUploadClick}
          loading={loading}
          loadingLabel="Create & Upload"
          disabled={!selectedFile || !isValidElementOptimizationForm(optimization)}
        >
          Create & Upload
        </AppButton>
      </DialogActions>
    </Dialog>
  );
};

export default UploadElementDialog;
